import ChevronRight from '@material-ui/icons/ChevronRight';
import { BaseClickable, palette, SolvTheme, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getProfileColorTheme } from '~/components/Account/util/colors';
import { profileCanAccessPremium } from '~/components/Membership/utils';
import { isActiveMembership } from '~/core/util/account';
import { colors } from '../../../../constants/colors';
import useToggleRuntimeVariable from '../../../../hooks/runtime/useToggleRuntimeVariable';
import Avatar from '../../../Account/Profile/Avatar';
import { useProfileBadge } from '../../../HeaderV2/components/AccountDrawer/hooks';
import Badge from '../../../SolvPatternLibrary/Icons/Badge';
import AccountViews from '../../constants/AccountViews';
import useSelectUserProfile from '../../hooks/useSelectUserProfile';
import useUpcomingAppointment from '../../hooks/useUpcomingAppointment';
import useHasDivider from './hooks/useHasDivider';

const ProfileBadge = styled(Badge).withConfig({ componentId: 'sc-169yu47-0' })`
  height: 14px;
  width: 14px;
  z-index: 1;
  padding: 2px;
`;

const ChevronDown = styled(ChevronRight).withConfig({ componentId: 'sc-169yu47-1' })`
  color: ${colors.purpletrator};
  font-size: 12px;
  transform: rotate(${Math.PI / /*🙂 ➡️ 🙃*/ 2}rad);
  margin-left: 8px;
`;

const SolvPlusChevronDown = styled(ChevronDown).withConfig({ componentId: 'sc-169yu47-2' })`
  color: ${palette.gray[600]};
`;

const PaddedAvatar = styled(Avatar).withConfig({ componentId: 'sc-169yu47-3' })`
  .avatar {
    border: solid 3px white;
    box-shadow: ${SolvTheme.shadows.xs};
    cursor: default;
  }
`;

type Props = {
  userProfileId: string;
  viewName?: string;
};

const Header = (props: Props) => {
  const { viewName, userProfileId } = props;

  const hasDivider = useHasDivider(viewName);
  const userProfile = useSelectUserProfile(userProfileId);
  const [, toggleAccountDrawerOpen] = useToggleRuntimeVariable('account-drawer');
  const hasBadge = !!useProfileBadge().length;
  const firstName = userProfile?.first_name || userProfile?.firstName;
  const accountSummary = useSelector((state) => state.account.summary);

  const shouldShowMembershipBranding =
    isActiveMembership(accountSummary) && profileCanAccessPremium(userProfile);

  const { t } = useTranslation(['account']);

  const { isFetching, appointment } = useUpcomingAppointment(userProfileId);

  const title =
    viewName === AccountViews.GetCare
      ? t('account:header.getCare', { FIRST_NAME: firstName })
      : firstName;

  const subtitle = t(
    appointment ? 'account:header.upcomingVisit' : 'account:header.noUpcomingVisit',
    { FIRST_NAME: firstName }
  );

  return (
    <Stack
      bg={shouldShowMembershipBranding ? 'teal-xlight' : 'grape-xlight'}
      style={{ boxShadow: hasDivider ? 'inset 0 -1px 0 0 #ECECEC' : 'none' }}
    >
      <Stack maxWidth={SolvTheme.breakpoints.lg} mx="auto" p={3} pb={2} row space={2} width="100%">
        <BaseClickable onClick={toggleAccountDrawerOpen} position="relative">
          {hasBadge && <ProfileBadge />}
          <PaddedAvatar
            className="avatar"
            profileColor={
              getProfileColorTheme(accountSummary, userProfile?.user_profile_id)?.primary
            }
            size={44}
            userProfile={userProfile}
          />
        </BaseClickable>

        <Stack width="100%">
          <BaseClickable
            alignItems="center"
            contentProps={{ justifyContent: 'flex-start' }}
            display="flex"
            justifyContent="flex-start"
            onClick={toggleAccountDrawerOpen}
            zIndex={30}
          >
            <Stack alignItems="center" row space={0.5}>
              <Text
                fontWeight="medium"
                textColor={shouldShowMembershipBranding ? 'gray-600' : 'grape-dark'}
                variant="h3"
              >
                {title}
              </Text>
              {shouldShowMembershipBranding && (
                <img src="/images/membership/logo_flat.svg" width="30px" />
              )}
            </Stack>

            {shouldShowMembershipBranding ? <SolvPlusChevronDown /> : <ChevronDown />}
          </BaseClickable>
          {viewName === AccountViews.GetCare && (
            <>
              {isFetching ? (
                <Stack alignItems="center" row space={1}>
                  <Spinner color={shouldShowMembershipBranding ? 'teal' : 'grape'} size="xs" />
                  <Text textColor={shouldShowMembershipBranding ? 'teal-dark' : 'grape-dark'}>
                    {t('account:header.loadingVisits', { FIRST_NAME: firstName })}
                  </Text>
                </Stack>
              ) : (
                <Text
                  textColor={shouldShowMembershipBranding ? 'teal-dark' : 'grape-dark'}
                  width="100%"
                >
                  {subtitle}
                </Text>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Header;
